import { MODULES } from "./constants";
import { doPOST } from "./HttpUtils";
import { api } from "../services/api/apiHandler";

export function isError(e: unknown): e is Error {
  return e instanceof Error;
}



export const moduleRoles = {
  "/dashboard": [MODULES.SUPERVISOR, MODULES.USER, MODULES.ADMIN, MODULES.HR, MODULES.DOCTOR],
  "/stats": [MODULES.HR, MODULES.ADMIN],
  "/patient-list": [ MODULES.SUPERVISOR, MODULES.DOCTOR, MODULES.HR, MODULES.LABORATORY, MODULES.ADMIN],
  "/patient-info/1": [ MODULES.SUPERVISOR, MODULES.DOCTOR, MODULES.ADMIN],
  "/profile": [MODULES.DOCTOR, MODULES.ADMIN, MODULES.HR],
  "/appointments": [MODULES.SUPERVISOR, MODULES.DOCTOR, MODULES.HR, MODULES.LABORATORY, MODULES.ADMIN],
  "/doctor-list": [MODULES.HR, MODULES.ADMIN],
  "/laboratory-list": [MODULES.HR, MODULES.ADMIN],
  "/admin": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/company": [MODULES.ADMIN],
  "/push-notification": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/wellness-events": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/departments": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/services": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/offers": [MODULES.SUPERVISOR, MODULES.MARKETING],
  "/hr-list": [MODULES.ADMIN],
  "/report": [MODULES.DOCTOR, MODULES.LABORATORY, MODULES.SUPERVISOR, MODULES.ADMIN],
  "/service-images": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/gallery-images": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN  ],
  "/event": [ MODULES.ADMIN, MODULES.HR],
  // "/event": [MODULES.ADMIN, MODULES.SUPERVISOR,MODULES.MARKETING],
  "/packages": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/forms": [MODULES.SUPERVISOR, MODULES.ADMIN],
  "/blogs": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/second-opinion": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/ewa-package": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/corporate-plan": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/surgery": [MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/superblogs": [ MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.ADMIN],
  "/company-external-packages": [ MODULES.SUPERVISOR, MODULES.ADMIN],
  "/vendors": [ MODULES.SUPERVISOR, MODULES.ADMIN],
  "/marketing": [ MODULES.SUPERVISOR, MODULES.ADMIN],
  "/perks": [MODULES.ADMIN],
  "/email-templates": [MODULES.ADMIN],
  "/banners": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.USER, MODULES.HR, MODULES.ADMIN],
  "/health-tips": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.MARKETING, MODULES.USER, MODULES.ADMIN],
  "/users": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.HR, MODULES.ADMIN],
  "/partners": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.ADMIN],
  "/slots": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.DOCTOR, MODULES.ADMIN],
  "/coupons": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.HR, MODULES.ADMIN],
  "/bookings": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.HR, MODULES.VENDOR],
  "/invoices": [MODULES.ADMIN],
  "/alyve": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.HR],
  "/alyve/:id": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.HR],
  "/payouts": [],
  "/notifications": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.ADMIN],
  "/app-versions": [MODULES.ADMIN],
  "/specialists": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.HR, MODULES.ADMIN],
  "/wellness-package": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.ADMIN],
  "/corporates": [MODULES.ADMIN, MODULES.SUPERVISOR],
  "/external-packages": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.USER, MODULES.VENDOR],
  "/public-external-catalog": [MODULES.ADMIN, MODULES.SUPERVISOR],
  "/camps": [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.HR],
}

export const normalizeRoles = (roles: string[] | string | null | undefined) =>
  Array.isArray(roles) ? roles : (roles ? [roles] : []);

export const hasRole = (roles: string[] | string | null | undefined, role: string) =>
  normalizeRoles(roles).includes(role);

export const isHRUser = (userData: any) => hasRole(userData?.role, MODULES.HR);

export const isVendorUser = (userData: any) =>
  hasRole(userData?.role, MODULES.VENDOR) ||
  hasRole(userData?.backendRole, MODULES.VENDOR);

export const getVendorIntegration = (userData: any) =>
  userData?.integration ||
  userData?.vendorIntegration ||
  userData?.vendor?.integration ||
  "";

export const isDoctorUser = (userData: any) =>
  hasRole(userData?.role, MODULES.DOCTOR) ||
  hasRole(userData?.role, 'specialist') ||
  hasRole(userData?.role, MODULES.SPECIALIST);

export const getUserSpecialistId = (userData: any) =>
  userData?.specialistId ||
  userData?._id ||
  userData?.id ||
  "";

export const getUserCorporateId = (userData: any) =>
  userData?.corporateId ||
  userData?.company ||
  userData?.corporate?._id ||
  userData?.corporate?.id ||
  "";

export const canEditModule = (userData: any, module: string | null | undefined) => {
  if (!module) return true;
  if (isVendorUser(userData)) return false;
  if (!isHRUser(userData)) return true;
  return module === MODULES.USER;
}

export const canCreateModule = (userData: any, module: string | null | undefined) => {
  if (!module) return true;
  if (isVendorUser(userData)) return false;
  if (!isHRUser(userData)) return true;
  return module === MODULES.USER;
}

export const canDeleteModule = (userData: any, module: string | null | undefined) => {
  if (!module) return true;
  if (isVendorUser(userData)) return false;
  if (!isHRUser(userData)) return true;
  return false;
}


export const hasAccess = (roles: string[] | string | null | undefined, module: string | null = null) => {
  const pathname = module ?? window.location.pathname;
  const accessPath = pathname.startsWith('/alyve/') ? '/alyve/:id' : pathname;

  // Normalize roles to an array
  const normalizedRoles = normalizeRoles(roles);

  if (accessPath in moduleRoles) {
    const allowedRoles = moduleRoles[accessPath as keyof typeof moduleRoles];
    if (allowedRoles) {
      return allowedRoles.some((r: any) => normalizedRoles.includes(r));
    }
    return false;
  }
  return true; // If path not in moduleRoles, allow access by default or define your policy
}


export const uploadFile = async (data: any, files: any) => {
  const formData = new FormData();

  Array.from(files).forEach((file: any) => {
    formData.append('files', file);
  });

  Object.keys(data).forEach((key) => {
    formData.append(key, data[key]);
  });

  const response = await doPOST('/file/upload', formData);

  return response;
}

export const downloadFile = async (src: string) => {
  try {
    const response = await api({
      url: src,
      headers: {
        Authorization: localStorage.getItem("BearerToken") || '',
      },
      withCredentials: false,
      responseType: "blob",
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", src.split("/").pop() || "file");
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};


export const isAdminOrSuperVisorOrHR = (userData: any) => {
  const roles = normalizeRoles(userData?.role);
  return [MODULES.ADMIN, MODULES.SUPERVISOR, MODULES.HR].some(role => roles.includes(role));
}

export const omitEmptyOptionalFields = <T extends Record<string, any>>(payload: T): T => {
  const cleanedEntries = Object.entries(payload).flatMap(([key, value]) => {
    if (value === undefined || value === null) {
      return [];
    }

    if (typeof value === "number" && Number.isNaN(value)) {
      return [];
    }

    if (typeof value === "string") {
      const trimmedValue = value.trim();
      return trimmedValue === "" ? [] : [[key, trimmedValue]];
    }

    if (Array.isArray(value)) {
      return [[key, value]];
    }

    if (typeof value === "object") {
      const nestedValue = omitEmptyOptionalFields(value);
      return Object.keys(nestedValue).length === 0 ? [] : [[key, nestedValue]];
    }

    return [[key, value]];
  });

  return Object.fromEntries(cleanedEntries) as T;
}


export const timeConverter = {
  toHHMM: (hhmmss: string) => {
    if (!hhmmss) return '';
    const [hh = '00', mm = '00'] = hhmmss.split(':');
    return `${hh}${mm}`;
  },

  toHHMMSS: (hhmm: string) => {
    if (!hhmm || hhmm.length < 3) return '00:00:00';
    const hh = hhmm.slice(0, 2).padStart(2, '0');
    const mm = hhmm.slice(2).padStart(2, '0');
    return `${hh}:${mm}:00`;
  }
};
